import React, { useEffect, useState } from 'react';
import { Building2, Lock, Eye } from 'lucide-react';
import { providerService, ProviderBusiness } from 'api/services/provider.service';

interface Props {
  value: string;
  onChange: (organizationId: string) => void;
  className?: string;
}

/**
 * Which business a service provider is looking at.
 *
 * <p>Renders nothing for a user who is not a provider — the endpoint answers 403 and
 * there is no business to choose between, so a disabled dropdown would be furniture.
 *
 * <p>A business that has withdrawn provider access is SHOWN and disabled rather than
 * omitted. A missing entry reads as "no such business" and sends somebody looking for
 * a bug; "you cannot open this one" is the actual fact.
 */
export const BusinessSwitcher: React.FC<Props> = ({ value, onChange, className }) => {
  const [businesses, setBusinesses] = useState<ProviderBusiness[]>([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      const r = await providerService.businesses();
      if (!alive || !r.success) return;
      const rows = r.data || [];
      setBusinesses(rows);
      // Preselect the first business a provider can actually open. Their own service
      // org owns no agents, no numbers and no calls, so defaulting to it shows an
      // empty screen that looks like a fault.
      if (!value) {
        const first = rows.find((b) => b.providerAccessAllowed !== false);
        if (first) onChange(first.id);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  if (businesses.length === 0) return null;

  const current = businesses.find((b) => b.id === value);

  return (
    <div className={`flex flex-col gap-2 ${className || ''}`}>
      {/* Says WHOSE data is on screen.
          Deliberately rendered here and not as a global banner in the layout: only
          the screens that pass this selection to their API calls are actually
          showing that business. A layout-wide banner would announce "acting as X"
          over pages still showing your own organisation, which is a worse failure
          than having no banner at all. */}
      {current && (
        <div className="flex items-start gap-2 text-xs rounded-lg px-3 py-2 bg-violet-50 text-violet-900 border border-violet-200
                        dark:bg-violet-900/20 dark:text-violet-200 dark:border-violet-800">
          <Eye className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Viewing <b>{current.name}</b> as their service provider. What you see and change here
            belongs to them, and this access is recorded.
          </span>
        </div>
      )}

      <label className="inline-flex items-center gap-2 text-sm">
      <Building2 className="w-4 h-4 text-gray-400" />
      <span className="text-gray-500 dark:text-gray-400">Business</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
      >
        {businesses.map((b) => (
          <option key={b.id} value={b.id} disabled={b.providerAccessAllowed === false}>
            {b.name}{b.providerAccessAllowed === false ? ' — access withdrawn' : ''}
          </option>
        ))}
      </select>
      {current?.providerAccessAllowed === false && (
        <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
          <Lock className="w-3.5 h-3.5" /> this business has withdrawn access
        </span>
      )}
      </label>
    </div>
  );
};
